import { useAnalytics } from '~/client/hooks/gallery/useAnalytics'
import { ANALYTICS_EVENT_ACTIONS, ANALYTICS_LABEL } from '~/shared/constants'
import { ActionInfo } from '~/client/components/Gallery/DesignPersonalization/util'
import { instrumenter } from '~/client/utils/instrumentation'
import { PAGE_ACTION_TYPES } from '~/client/constants'
import { useMemo } from 'react'

export interface PersonalizationAnalytics {
  textFocus: (purposeName: string) => void
  flyoutClosed: () => void
  imageSelected: () => void
  imageDeselected: () => void
  imageDeleted: () => void
  imageUploadStarted: () => void
  imageUploadClicked: () => void
  imageUploadCompleted: () => void
  imageUploadError: (error?: Error) => void
  selectedImagesCleared: () => void
  imageModalClosed: () => void
  changeImage: () => void
  addImage: () => void
  uploadsModalClosed: () => void
  sidebarViewed: () => void
  mobileCtaViewed: () => void
  modalImagesApplied: (selectedUploadsLength: number, isMobile?: boolean) => void
  mobileImageUploadButtonClick: (selectedUploadsLength: number) => void
  recordImageLoadingSuccess: (actionInfo: ActionInfo) => void
  recordImageLoadingError: (actionInfo: ActionInfo) => void
  flyOutViewed: () => void
  textPersonalized: (purposeName: string, isMobile?: boolean) => void
}

export const usePersonalizationAnalytics = (): PersonalizationAnalytics => {
  const analytics = useAnalytics()

  const textFocus = (purposeName: string): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.BUTTON_CLICKED,
      eventLabel: 'Text Field Selected',
      eventDetail: `Text Field Selected: ${purposeName}`,
      ...analytics.getPageProperties(),
    })
  }

  const flyOutViewed = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.FLY_OUT_VIEWED,
      eventLabel: 'Personalization Button',
      eventDetail: 'Personalization Mobile Modal Open',
      ...analytics.getPageProperties(),
    })
  }

  const sidebarViewed = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.PERSONALIZATION_VIEWED,
      eventLabel: ANALYTICS_LABEL.PERSONALIZATION_VIEWED,
      eventDetail: 'Personalization Sidebar Viewed',
      ...analytics.getPageProperties(),
    })
  }

  const mobileCtaViewed = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.PERSONALIZATION_VIEWED,
      eventLabel: ANALYTICS_LABEL.PERSONALIZATION_VIEWED,
      eventDetail: 'Personalization Mobile CTA Viewed',
      ...analytics.getPageProperties(),
    })
  }

  const flyoutClosed = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.FLY_OUT_CLOSED,
      eventLabel: 'Personalization Close Button',
      eventDetail: 'Personalization Mobile Modal Closed',
      ...analytics.getPageProperties(),
    })
  }

  const uploadsModalClosed = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.FLY_OUT_CLOSED,
      eventLabel: 'Personalization modal closed',
      eventDetail: `Personalization modal closed`,
      ...analytics.getPageProperties(),
    })
  }

  const imageUploadStarted = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.IMAGE_UPLOADED,
      eventLabel: 'Image Upload Started',
      eventDetail: `Image Upload Started`,
      ...analytics.getPageProperties(),
    })
  }

  const imageSelected = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.BUTTON_CLICKED,
      eventLabel: 'Personalization modal image selected',
      eventDetail: 'Personalization modal image selected',
      ...analytics.getPageProperties(),
    })
  }

  const imageDeselected = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.BUTTON_CLICKED,
      eventLabel: 'Personalization modal image deselected',
      eventDetail: 'Personalization modal image deselected',
      ...analytics.getPageProperties(),
    })
  }

  const imageDeleted = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.BUTTON_CLICKED,
      eventLabel: 'Image Deleted',
      eventDetail: `Image Deleted`,
      ...analytics.getPageProperties(),
    })
  }

  const imageUploadError = (error?: Error): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.IMAGE_UPLOADED,
      eventLabel: 'Image Upload Error',
      eventDetail: `Image Upload Error: ${error}`,
      ...analytics.getPageProperties(),
    })
  }

  const imageUploadClicked = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.BUTTON_CLICKED,
      eventLabel: 'Image Upload Clicked',
      eventDetail: 'Image Upload Clicked',
      ...analytics.getPageProperties(),
    })
  }

  const imageUploadCompleted = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.IMAGE_UPLOADED,
      eventLabel: 'Image Upload Success',
      eventDetail: 'Image Upload Success',
      ...analytics.getPageProperties(),
    })
  }

  const selectedImagesCleared = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.BUTTON_CLICKED,
      eventLabel: 'Personalization modal selected images cleared',
      eventDetail: 'Personalization modal selected images cleared',
      ...analytics.getPageProperties(),
    })
  }

  const imageModalClosed = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.FLY_OUT_CLOSED,
      eventLabel: 'Personalization Image Modal Close Button',
      eventDetail: 'Personalization Image Modal Closed',
      ...analytics.getPageProperties(),
    })
  }

  const changeImage = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.FLY_OUT_CLICKED,
      eventLabel: 'Personalization Upload Modal',
      eventDetail: 'Personalization Upload Modal: Change Image',
      ...analytics.getPageProperties(),
    })
  }

  const addImage = (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.FLY_OUT_CLICKED,
      eventLabel: 'Personalization Upload Modal',
      eventDetail: 'Personalization Upload Modal: Add Image',
      ...analytics.getPageProperties(),
    })
  }

  const modalImagesApplied = (selectedUploadsLength: number, isMobile?: boolean): void => {
    const eventLabel = isMobile ? 'Personalization modal images applied (mobile)' : 'Personalization modal images applied'
    const eventDetail = isMobile
      ? `${selectedUploadsLength} Mobile personalization modal images applied`
      : `${selectedUploadsLength} Personalization modal images applied`

    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.BUTTON_CLICKED,
      eventLabel,
      eventDetail,
      ...analytics.getPageProperties(),
    })
  }

  const mobileImageUploadButtonClick = (selectedUploadsLength: number): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.BUTTON_CLICKED,
      eventLabel: 'Mobile image upload modal preview button clicked',
      eventDetail: `${selectedUploadsLength} images selected`,
      ...analytics.getPageProperties(),
    })
  }

  const textPersonalized = (purposeName: string, isMobile?: boolean): void => {
    const eventLabel = isMobile ? 'Text Personalized (mobile)' : 'Text Personalized (manual input)'
    const eventDetail = isMobile ? `Text Personalized (mobile): ${purposeName}` : `Text Personalized: ${purposeName}`

    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.BUTTON_CLICKED,
      eventLabel,
      eventDetail,
      ...analytics.getPageProperties(),
    })
  }

  const recordImageLoadingSuccess = (actionInfo: ActionInfo): void => {
    instrumenter.recordPageAction(PAGE_ACTION_TYPES.IMAGE_LOADING_SUCCESS, actionInfo)
  }
  const recordImageLoadingError = (actionInfo: ActionInfo): void => {
    instrumenter.recordPageAction(PAGE_ACTION_TYPES.IMAGE_LOADING_FAILURE, actionInfo)
  }

  return useMemo(
    () => ({
      textFocus,
      flyoutClosed,
      imageUploadStarted,
      imageSelected,
      imageDeselected,
      imageDeleted,
      imageUploadError,
      imageUploadClicked,
      imageUploadCompleted,
      selectedImagesCleared,
      imageModalClosed,
      changeImage,
      addImage,
      uploadsModalClosed,
      sidebarViewed,
      modalImagesApplied,
      mobileImageUploadButtonClick,
      recordImageLoadingSuccess,
      recordImageLoadingError,
      flyOutViewed,
      textPersonalized,
      mobileCtaViewed,
    }),
    [analytics],
  )
}
